import { isEmpty } from "lodash";
import React, { createContext, useContext, useEffect, useRef, useState } from "react";
import AuthApi from "src/api/webapp/auth-api";
import CustomerServiceChat from "src/components/CustomerServiceChat/CustomerServiceChat";
import AiAgent from "src/modules/ai-agent";
import { ChatOption } from "src/modules/ai-agent/enums";
import { Storage } from "src/services";
import { Cookie, getCookieByKey, setCookie } from "src/utils";
import { useGlobalContext } from "./global.context";

interface ChatContextType {
  option: ChatOption;
  setOption: (opt: ChatOption) => void;
  chatToken: string;
  setChatToken: (chatToken: string) => void;
  lite: boolean;
  setLite: (lite: boolean) => void;
  email: string;
  setEmail: (email: string) => void;
  isAdmin: boolean;
  setIsAdmin: (isAdmin: boolean) => void;
}

const ChatContext = createContext<ChatContextType | undefined>(undefined);

export const ChatProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isYappUser } = useGlobalContext();

  const initialized = useRef(false);

  const [option, setOption] = useState(ChatOption.NONE);
  const [chatToken, setChatToken] = useState("");
  const [lite, setLite] = useState(true);
  const [email, setEmail] = useState("");
  const [isAdmin, setIsAdmin] = useState(false);

  useEffect(() => {
    const hasCookie = getCookieByKey(Cookie.Accepted) === "true";
    if (hasCookie) {
      if (initialized.current) return;
      initialized.current = true;
      const token = Storage.getToken();
      if (isEmpty(token)) {
        generateChatToken();
      } else {
        setEmail(Storage.getUserEmail());
        generateChatToken(Storage.getCompanyCode(), Storage.getUserId().toString());
      }
      const showAiChat = localStorage.getItem("show_ai_chat");
      setOption(showAiChat == "1" ? ChatOption.CHAT : showAiChat == "2" ? ChatOption.HISTORY : ChatOption.NONE);
    }
  }, []);

  const generateChatToken = (companyCode?: string, userId?: string) => {
    let sessionIdCookie = getCookieByKey(Cookie.QRSession);
    if (!sessionIdCookie) {
      sessionIdCookie = crypto.randomUUID();
      setCookie(Cookie.QRSession, sessionIdCookie, 60);
    }
    AuthApi.generateSessionToken(sessionIdCookie, companyCode || sessionIdCookie, userId || sessionIdCookie)
      .then((sharedToken) => {
        if (sharedToken) {
          setChatToken(sharedToken);
          if (companyCode) {
            setLite(false);
          }
        }
      })
      .catch((err) => {
        console.error(err);
      });
  };

  return (
    <ChatContext.Provider
      value={{ option, setOption, chatToken, setChatToken, lite, setLite, email, setEmail, isAdmin, setIsAdmin }}
    >
      {children}
      <CustomerServiceChat email={email} />
      {!isYappUser && <AiAgent isAdmin={isAdmin} />}
    </ChatContext.Provider>
  );
};

export const useChat = () => {
  const ctx = useContext(ChatContext);
  if (!ctx) throw new Error("useChat deve ser usado dentro de ChatProvider");
  return ctx;
};
